import React from 'react';
import Collapse from '@mui/material/Collapse';
import type { Constituent } from '@/graph/domain/types';
import { generateMaterialLabel } from '@/graph/domain/labelGen';

interface ConstituentBracketProps {
  constituents: Constituent[];
  collapsedExpr?: string;
  showExpandedWeights?: boolean;
  collapsedMode?: 'expr' | 'icon';
}

const formatWeight = (value: number): string => Number.isFinite(value) ? value.toFixed(2) : '0.00';

const flatNameExpr = (constituents: Constituent[]): string => {
  if (!constituents || constituents.length === 0) return '';
  return `(${constituents.map((c) => c.name).join(' + ')})`;
};

export const ConstituentBracket: React.FC<ConstituentBracketProps> = ({
  constituents,
  collapsedExpr,
  showExpandedWeights = true,
  collapsedMode = 'expr',
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!constituents || constituents.length === 0) {
    return null;
  }

  const sorted = [...constituents].sort((a, b) => b.weight - a.weight);
  const collapsed = collapsedExpr && collapsedExpr.trim() !== '' ? collapsedExpr : generateMaterialLabel(sorted);
  const toggleLabel = collapsedMode === 'icon'
    ? (expanded ? '(-)' : '(+)')
    : `(${collapsed})`;
  const iconExpandedExpr = flatNameExpr(constituents);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        role="button"
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse constituents' : 'Expand constituents'}
      >
        {collapsedMode === 'icon' ? (
          <span style={{ color: '#2563eb' }}>{toggleLabel}</span>
        ) : (
          toggleLabel
        )}
      </span>
      <Collapse in={expanded} timeout="auto" orientation="horizontal" unmountOnExit>
        <span style={{ display: 'inline' }}>
          {collapsedMode === 'icon'
            ? iconExpandedExpr
            : `(${sorted
                .map((item) => showExpandedWeights ? `${item.name} ${formatWeight(item.weight)}` : item.name)
                .join(' + ')})`}
        </span>
      </Collapse>
    </span>
  );
};
